'use client'

import { GrowthBookClient } from '@growthbook/growthbook'
import type { Attributes, FeatureApiResponse, UserContext } from '@growthbook/growthbook'
import { track } from '@shared/analytics'
import { useEffect, useMemo, useRef } from 'react'

interface ExperimentTrackerProps {
  featureIds: string[]
  attributes: Attributes
  payload: FeatureApiResponse
}

// 모듈 레벨 싱글턴: 브라우저 탭 단위로 유지되며 SPA 내비게이션 간 dedup을 보장
const growthbook = new GrowthBookClient()
const trackedExperiments = new Set<string>()

export const ExperimentTracker = ({ featureIds, attributes, payload }: ExperimentTrackerProps) => {
  const prevEntityIdRef = useRef<string | undefined>(undefined)

  // 서버에서 매 요청마다 새 객체가 내려오므로 JSON 직렬화로 참조 안정화
  const stableKey = useMemo(
    () => JSON.stringify({ featureIds, attributes }),
    [JSON.stringify(featureIds), JSON.stringify(attributes)],
  )

  useEffect(() => {
    if (featureIds.length === 0) return

    // 유저가 변경되면 (로그아웃→재로그인 등) dedup Set 초기화
    const currentEntityId = String(attributes.id ?? '')
    if (prevEntityIdRef.current !== undefined && prevEntityIdRef.current !== currentEntityId) {
      trackedExperiments.clear()
    }
    prevEntityIdRef.current = currentEntityId

    try {
      growthbook.initSync({ payload })

      const ctx: UserContext = {
        attributes,
        trackingCallback: (experiment, result) => {
          track('experiment_viewed', {
            experiment_id: experiment.key,
            variation_id: result.variationId,
            variation_key: String(result.key),
            entity_id: currentEntityId,
          })
        },
        trackedExperiments,
      }

      featureIds.forEach((fid) => {
        growthbook.evalFeature(fid, ctx)
      })
    } catch (error) {
      console.warn('[ExperimentTracker] Failed to evaluate experiments', error)
    }
  }, [stableKey])

  return null
}
